import * as React from 'react';
import { DateTimeFormatter, LocalDate } from 'js-joda';

import * as globalEventsHelper from '@tock/shared/utils/globalEventsHelper';
import { DateAvailabilityObject } from 'types';
import { DisplayDate } from '@tock/time';
import { IWithContextProps, withContext } from '@tock/coretypes';
import { IconChevronDown } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { Typography } from 'omakase';
import { css } from '@emotion/react';
import { preventBubbleOutside } from '@tock/shared/utils/eventUtils';
import BaseComponent from '@tock/shared/components/BaseComponent';
import ConsumerCalendar from 'components/calendar/ConsumerCalendar';
import _ from 'lodash';

interface Props {
  availableDates?: DateAvailabilityObject;
  dateClassName: string;
  hasExpandedCalendar?: boolean;
  hasInitialFocus?: boolean;
  lockedDates: DateAvailabilityObject;
  offeredDates?: DateAvailabilityObject;
  onSelectDate(date: string): void;
  onToggleExpandedCalendar?(event: React.MouseEvent<HTMLElement>): void;
  openDates: DateAvailabilityObject;
  selectedDate: LocalDate;
  soldDates?: DateAvailabilityObject;
}

interface State {
  showDateSelector: boolean;
  dateSelectorId: string;
}

const ariaDateFormatter = DateTimeFormatter.ofPattern('M/d/yyyy');
class SearchBarDateSection extends BaseComponent<Props & IWithContextProps, State> {
  selectDateRef: React.RefObject<HTMLButtonElement>;

  constructor(props: Props & IWithContextProps) {
    super(props);
    this.state = {
      showDateSelector: false,
      dateSelectorId: _.uniqueId('date_selector_'),
    };
    this.selectDateRef = React.createRef();
  }

  handleToggleDateSelector(event: React.MouseEvent<HTMLElement>) {
    const { showDateSelector } = this.state;

    if (showDateSelector) {
      this.handleHideDateSelector();
    } else if (this.props.hasExpandedCalendar && this.props.onToggleExpandedCalendar) {
      this.props.onToggleExpandedCalendar(event);
    } else {
      event.nativeEvent.stopImmediatePropagation();
      globalEventsHelper.addGlobalClickEvent(this.handleHideDateSelector, 'popover');
      this.setState({
        showDateSelector: !showDateSelector,
      });
    }
  }

  handleHideDateSelector() {
    globalEventsHelper.removeGlobalClickEvent(this.handleHideDateSelector, 'popover');
    this.setState({
      showDateSelector: false,
    });
  }

  handleSelectDate(date: string) {
    this.handleHideDateSelector();
    this.props.onSelectDate(date);
  }

  // We do the focus-related componentDidMount only in a setTimeout after 600 ms,
  // because this content may be presented in an iframe. If it is, and:
  //
  // * If voiceover is turned on, and the user is using Safari, some kind of
  // setTimeout delay is necessary to avoid a problem where the focus gets trapped
  // outside the dialog. Any amount of delay seems to be sufficient here.
  //
  // * If voiceover is turned on, and the user is using Chrome, focusing one of
  // these buttons too soon causes an issue where navigating the page content
  // will loop infinitely within a small part of the page instead of including
  // all pageable elements. For this problem, a small delay is not enough;
  // experimentally, 600 ms is consistently sufficient.
  componentDidMount() {
    const { hasInitialFocus } = this.props;
    window.setTimeout(() => {
      if (hasInitialFocus) {
        this.selectDateRef.current?.focus();
      }
    }, 600);
  }

  render() {
    const {
      availableDates,
      lockedDates,
      dateClassName,
      offeredDates,
      openDates,
      selectedDate,
      soldDates,
      context: { isFeatureEnabled },
    } = this.props;
    const { showDateSelector, dateSelectorId } = this.state;
    const isSakeIconSetEnabled = isFeatureEnabled('SAKE_ICON_SET');
    return (
      <div className={dateClassName}>
        <button
          css={[
            isSakeIconSetEnabled &&
              css`
                // TODO: add to SCSS styles for consistency after FF is launched
                padding-right: 1.2rem;
              `,
          ]}
          className="SearchBar-sectionDropdown"
          aria-expanded={showDateSelector}
          aria-controls={dateSelectorId}
          aria-label={
            selectedDate
              ? `Desired reservation date, current selection is ${selectedDate.format(
                  ariaDateFormatter
                )}`
              : 'Desired reservation date'
          }
          title="Desired reservation date"
          onClick={this.handleToggleDateSelector}
          data-testid="reservation-date-button"
          ref={this.selectDateRef}
        >
          <div className="SearchBar-sectionDropdownContent">
            <div
              css={css`
                align-items: flex-start;
                display: flex;
                flex-direction: column;
              `}
            >
              <Typography variant="caption" color="fgSubtle">
                Date
              </Typography>
              <Typography variant="body02" color="fgDefault">
                {selectedDate ? (
                  <DisplayDate dateValue={selectedDate} displayMonthAs="short" />
                ) : (
                  'Select date'
                )}
              </Typography>
            </div>
            <IconMigrationUtil
              legacyIcon={
                <i className="SearchBar-sectionDropdownIcon tock-icon icon_small-arrow-down" />
              }
              newIcon={<IconChevronDown className="SearchBar-sectionDropdownIcon" />}
            />
          </div>
        </button>

        {showDateSelector && (
          <button
            id={dateSelectorId}
            data-testid="reservation-search-calendar"
            className="SearchBarDropdown"
            onClick={preventBubbleOutside}
          >
            <ConsumerCalendar
              availableDates={availableDates}
              lockedDates={lockedDates}
              offeredDates={offeredDates}
              openDates={openDates}
              selectedDate={selectedDate ? selectedDate.toString() : undefined}
              soldDates={soldDates}
              wideView={false}
              onSelect={this.handleSelectDate}
            />
          </button>
        )}
      </div>
    );
  }
}

export default withContext(SearchBarDateSection);
